// callLogDupes.js — collapse Gmail double-delivery copies in activity feeds
//
// Gmail delivers the same email more than once — direct + via a group address,
// or once per ingested principal mailbox — and each copy carries its OWN
// per-mailbox gmail_message_id. Every call_log write guard keys on that id, so
// each copy passes the guard and the timeline shows identical entries twice.
// Measured in prod 2026-08-15: 996 duplicate groups across 7,144 email rows
// (~21% redundant). Collapse is display-time only — rows stay in the DB; the
// feed shows one entry per underlying email.
//
// Only contact_type='email' rows collapse. Two email rows are the same email
// when they share:
//   - gmail_message_id + call_type (one copy logged twice by racing writers), or
//   - from_email + contact_date (to the second) + summary + call_type
//     (two Gmail copies of one send — the Date header, sender and subject are
//     identical across copies; a genuinely distinct email can't share all three).
// call_type is part of both keys because an outbound reply row reuses the
// INBOUND message's gmail_message_id (inbox-actions send-reply) — email_in and
// email_out for the same message id are different events, not duplicates.
// Calls, meetings, notes, texts, email_open pings are never collapsed.

const LINK_COLS = [
  'linked_deals', 'linked_projects', 'linked_properties',
  'linked_opportunities', 'linked_companies', 'linked_contacts',
]

function epochSec(d) {
  const t = new Date(d).getTime()
  return Number.isFinite(t) ? Math.floor(t / 1000) : String(d || '')
}

// How much a row knows — the survivor should be the copy that carries the
// resolved contact, the body, and the entity links.
function richness(r) {
  let s = 0
  if (r.contact_id) s += 4
  if (r.email_body || r.email_body_html) s += 2
  for (const col of LINK_COLS) {
    if (Array.isArray(r[col])) s += Math.min(r[col].length, 3)
  }
  return s
}

// true when a should survive over b
function outranks(a, b) {
  const ra = richness(a), rb = richness(b)
  if (ra !== rb) return ra > rb
  // Earlier contact_date wins — the auto-filed row carries the email's real
  // Date header while a later confirm writes "now".
  const da = new Date(a.contact_date).getTime() || 0
  const db = new Date(b.contact_date).getTime() || 0
  if (da !== db) return da < db
  return String(a.created_at || '') < String(b.created_at || '')
}

function collapseBy(rows, keyOf) {
  const byKey = new Map() // key → index into out
  const out = []
  for (const row of rows) {
    const key = keyOf(row)
    if (key == null) { out.push(row); continue }
    const idx = byKey.get(key)
    if (idx == null) {
      byKey.set(key, out.length)
      out.push(row)
    } else if (outranks(row, out[idx])) {
      out[idx] = row // survivor keeps the first occurrence's position
    }
  }
  return out
}

/**
 * Collapse duplicate email entries in a call_log row list. Preserves input
 * order (survivor sits where the group's first row sat) and never touches
 * non-email rows. Safe on any feed's merged result.
 */
export function collapseEmailDupes(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return rows || []

  // Pass 1 — same Gmail copy logged twice (racing writers, sidebar + pipeline)
  const pass1 = collapseBy(rows, r => {
    if (r.contact_type !== 'email' || !r.gmail_message_id) return null
    return `id|${r.gmail_message_id}|${r.call_type || r.direction || ''}`
  })

  // Pass 2 — distinct Gmail copies of the same underlying email
  return collapseBy(pass1, r => {
    if (r.contact_type !== 'email' || !r.from_email || !r.contact_date) return null
    const summary = String(r.summary || '').trim()
    if (!summary) return null
    return [
      'em', String(r.from_email).toLowerCase(), epochSec(r.contact_date),
      summary, r.call_type || r.direction || '',
    ].join('|')
  })
}
